import { Link } from 'react-router-dom';
import { ApiError } from '@chicare/shared';

import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

/**
 * The error state for console screens.
 *
 * §11: "errors say what happened and never apologise vaguely." That matters
 * more here than anywhere else in the app, because the person reading it is
 * usually mid-incident and needs to know in one glance whether the problem is
 * their session, their permissions, or the API being down.
 *
 * So each case gets its own answer and its own next action — a single "Try
 * again" for all three would be wrong two times out of three.
 */
export function AdminError({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: unknown;
  onRetry: () => void;
}) {
  const apiError = error instanceof ApiError ? error : null;

  const { body, action } = explain(apiError, onRetry);

  return (
    <div className="mx-auto w-full max-w-[560px] px-4 py-12">
      <Card className="text-center">
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-danger-tint">
          <Icon name="alert" size={24} className="text-danger" />
        </div>

        <h1 className="m-0 font-display text-[19px] font-bold tracking-[-0.02em]">
          {apiError?.isUnauthenticated ? 'Your session has expired' : title}
        </h1>

        <p className="mx-auto mt-2 max-w-[42ch] text-[13px] leading-relaxed text-muted">{body}</p>

        <div className="mt-5 flex justify-center gap-2">{action}</div>

        {apiError && !apiError.isUnauthenticated ? (
          <p className="mt-4 font-mono text-[10.5px] text-faint">
            {apiError.code} · HTTP {apiError.status}
          </p>
        ) : null}
      </Card>
    </div>
  );
}

function explain(error: ApiError | null, onRetry: () => void) {
  if (error?.isUnauthenticated) {
    return {
      body: 'Console sessions do not last forever. Sign in again and you will come straight back here.',
      action: (
        <Link
          to="/admin/login"
          className="inline-flex min-h-11 items-center rounded-btn bg-blue px-5 text-[13.5px] font-bold text-white no-underline"
        >
          Sign in again
        </Link>
      ),
    };
  }

  if (error?.status === 403) {
    return {
      body: 'Your account does not have console access. Ask a super admin to change your role — it is not something that can be granted from this screen.',
      action: (
        <Link
          to="/"
          className="inline-flex min-h-11 items-center rounded-btn border border-line bg-white px-5 text-[13.5px] font-bold text-muted no-underline"
        >
          Back to ChiCare
        </Link>
      ),
    };
  }

  if (error?.code === 'NETWORK_UNREACHABLE') {
    return {
      body: 'We could not reach the ChiCare API at all. Check that the API is running, then try again.',
      action: (
        <Button icon="refresh" onClick={onRetry}>
          Try again
        </Button>
      ),
    };
  }

  return {
    body:
      error?.message ??
      'Something went wrong on our side. Nothing you were editing has been saved or lost.',
    action: (
      <Button icon="refresh" onClick={onRetry}>
        Try again
      </Button>
    ),
  };
}
